import './AwardNotifications.css';
import {useCallback,useEffect,useRef,useState} from 'react';
import type {Award,Card,Snapshot} from '@platform/contracts/x';
import {money,type AwardStreamBoundary} from '@platform/client';
import {WinEffects} from '@platform/broadcast/WinEffects';
import {createGameAudio} from '@platform/broadcast/gameAudio';
import {Button} from '@platform/ui/Button';
import {useCopy,useUi} from '@platform/ui/config';
import {CardBadge} from './CardBadge';

type Notice={key:string;drawId:string;currency:string;award:Award;cards:Pick<Card,'id'|'name'|'colorGroup'>[];startsAt:number;expiresAt:number};

// Announcements follow confirmed awards, never a prize estimate or a card's
// local markings. Only uninterrupted live observation produces notices.
// A timed Tombala + JP share one notice and one server deadline. Older
// profiles retain their bounded queue; history and earlier stages never replay.
export function AwardNotifications({state,stream,history=false,transition=null,sound=false}:{state:Snapshot;stream:AwardStreamBoundary;history?:boolean;transition?:Snapshot|null;sound?:boolean}){
 const t=useCopy(),ui=useUi();
 const ledger=useRef<{generation:number;drawId:string;ids:Set<string>}|null>(null);
 const celebrated=useRef('');
 const speech=useRef<ReturnType<typeof createGameAudio>|null>(null);
 const [queue,setQueue]=useState<Notice[]>([]),[visible,setVisible]=useState(!document.hidden);
 const [blocked,setBlocked]=useState(false);
 const [readyKey,setReadyKey]=useState('');
 useEffect(()=>{
  const awards=state.awards;
  if(!stream.ready||!visible){ledger.current=null;setQueue([]);return;}
  if(!ledger.current||ledger.current.generation!==stream.generation){
   ledger.current={generation:stream.generation,drawId:stream.drawId,ids:new Set(stream.awardIds)};
   setQueue([]);
  }
  const enqueue=(draw:Snapshot,fresh:Award[])=>{
   if(!fresh.length)return;
   const final=fresh.find(a=>a.stage==='full-house'),jp=fresh.find(a=>a.stage==='jackpot');
   // Jackpot includes the simultaneous Tombala payout, with just one scene/clip.
   const grouped=final&&jp?fresh.filter(a=>a!==final).map(a=>a===jp?{...jp,ownMinor:final.ownMinor+jp.ownMinor,cardIds:[...new Set([...final.cardIds,...jp.cardIds])]}:a):fresh;
   const notice=(award:Award,startsAt:number,expiresAt:number):Notice=>({
    key:JSON.stringify([draw.drawId,award.id]),drawId:draw.drawId,currency:draw.currency,award,startsAt,expiresAt,
    // Keep the confirmed draw's metadata through rollover. Other players' private
    // layouts/names are absent from the snapshot and must never be looked up.
    cards:award.cardIds.flatMap(id=>{
     const card=draw.cards.find(c=>c.id===id);
     if(award.ownMinor>0&&!(card&&card.ownMinor>0))return [];
     return [card??{id}];
    })
   });
   const pause=draw.awardPause;
   if(pause){
    // Relative server time avoids depending on the device's wall clock. No
    // countdown request, server timer or broadcast is created per player.
    const serverNow=Date.parse(draw.serverTimeUtc),now=Date.now();
    const remaining=Date.parse(pause.popupEndsAtUtc??pause.untilUtc)-serverNow;
    const startsAt=now+Math.max(0,Date.parse(pause.popupStartsAtUtc??pause.startedAtUtc)-serverNow);
    if(remaining<=0){setQueue([]);return;}
    setQueue(grouped.map(award=>notice(award,startsAt,now+remaining)));return;
   }
   setQueue([]); // A confirmed X award always includes its server presentation window.
  };
  // ADR-020: a spectator follows the next draw without waiting for the notice.
  // Preserve only fresh notices from the continuously observed previous draw,
  // including a final update batched with rollover. Reconnect baselines still
  // prevent historical catch-up; manual navigation clears this transition.
  if(ledger.current.drawId!==state.drawId){
   const carry=transition?.drawId===ledger.current.drawId;
   if(carry&&transition.tombala.status!=='Cancelled')enqueue(transition,transition.awards.filter(a=>!ledger.current!.ids.has(a.id)));
   else setQueue([]);
   ledger.current={generation:stream.generation,drawId:state.drawId,ids:new Set(awards.map(a=>a.id))};
   return;
  }
  // Recovering the final state of an earlier draw restores cards and amounts,
  // never new popups. Already observed live notices keep their bounded lifetime.
  if(history){awards.forEach(award=>ledger.current!.ids.add(award.id));return;}
  const fresh=awards.filter(award=>!ledger.current!.ids.has(award.id));
  fresh.forEach(award=>ledger.current!.ids.add(award.id));
  if(state.tombala.status==='Cancelled'){
   setQueue([]);return;
  }
  enqueue(state,fresh);
 },[state.drawId,state.awards,state.currency,state.tombala.status,state.awardPause,state.serverTimeUtc,stream,visible,history,transition,ui.controls.awardWinnerDurationMs,ui.controls.awardObserverDurationMs]);
 const pending=stream.ready&&visible&&(queue[0]?.drawId===state.drawId||queue[0]?.drawId===transition?.drawId)?queue[0]:undefined;
 const active=pending?.key===readyKey?pending:undefined,won=!!active&&active.award.ownMinor>0;
 const dismiss=useCallback((key:string)=>setQueue(previous=>previous[0]?.key===key?previous.slice(1):previous),[]);
 useEffect(()=>{
  const update=()=>{setVisible(!document.hidden);if(document.hidden)setQueue([]);};
  document.addEventListener('visibilitychange',update);return()=>document.removeEventListener('visibilitychange',update);
 },[]);
 useEffect(()=>{
  // Native modal dialogs make outside controls inert. Suppress the popup while
  // open, but keep its expiry running so closing a panel cannot replay history.
  const update=()=>setBlocked(!!document.querySelector('dialog[open]'));
  update();
  const observer=new MutationObserver(update);
  observer.observe(document.body,{subtree:true,childList:true,attributes:true,attributeFilter:['open']});
  return()=>observer.disconnect();
 },[]);
 useEffect(()=>{
  if(!pending){setReadyKey('');return;}
  const expire=()=>setQueue(previous=>previous.filter(notice=>notice.expiresAt>Date.now()));
  const show=setTimeout(()=>setReadyKey(pending.key),Math.max(0,pending.startsAt-Date.now()));
  const hide=setTimeout(expire,Math.max(0,pending.expiresAt-Date.now()));
  return()=>{clearTimeout(show);clearTimeout(hide);};
 },[pending?.key,pending?.startsAt,pending?.expiresAt]);
 useEffect(()=>{
  // A final two-clip announcement may finish just after its popup expires.
  // Mute, hide, modal, reconnect and unmount must still stop the whole sequence.
  return()=>{speech.current?.pause();speech.current=null;};
 },[sound,visible,blocked,stream.ready,stream.generation]);
 useEffect(()=>{
  if(!active)return;
  if(blocked||!visible||document.querySelector('dialog[open]')||celebrated.current===active.key)return;
  celebrated.current=active.key;
  if(!sound)return;
  const clip=active.award.stage==='jackpot'?'jackpot':active.award.stage==='full-house'?'tombala':'cinko';
  const final=clip==='tombala'||clip==='jackpot';
  const audio=createGameAudio(final?[ui.assets.awardSounds[clip],ui.assets.drawEndSound]:ui.assets.awardSounds[clip]);
  speech.current=audio;
  void audio.play().catch(()=>{});
  return()=>{if(!final||Date.now()<active.expiresAt)audio.pause();};
 },[active?.key,blocked,visible,won,sound]);
 const jackpot=active?.award.stage==='jackpot';
 return <div className="award-notifications" data-presentation={won?'winner':'observer'} hidden={!active||blocked}>
  <WinEffects eventId={active&&!blocked&&won?active.key:''} jackpot={jackpot}/>
  {active&&!blocked&&<section key={active.key} className={'award-notice '+(won?'award-notice--winner':'award-notice--observer')+(won&&jackpot?' award-notice--jackpot':'')} data-award-id={active.award.id} data-draw-id={active.drawId}>
    <Button className="award-notice-close" aria-label={t('award.dismiss')} onClick={()=>dismiss(active.key)}>×</Button>
    <div className="award-notice-content" role="status" aria-live="polite" aria-atomic="true">
     <h2 className="award-notice-stage">{t('prize.'+active.award.stage)}</h2>
     {won&&<span className="award-notice-symbol" aria-hidden="true">{jackpot?'✧':'✦'}</span>}
     <p className="award-notice-title">{t(won?'award.winnerTitle':active.award.fundedWinningCards===0?'x.unfundedAward':'award.observerTitle')}</p>
     {won&&<strong className="award-notice-amount">{money(active.award.ownMinor,active.currency)}</strong>}
     <div className="award-notice-cards" aria-label={t('award.winningCards',{count:active.cards.length})}>
      {active.cards.map(card=><CardBadge key={card.id} card={card}/>)}
     </div>
    </div>
   </section>}
  </div>;
}
